// SEQ-180 shared formatting and visual-style helpers for the P&L analytics
// page. Plain functions and style objects only (no components), so this
// module can sit alongside components without tripping the
// react-refresh/only-export-components rule, and so it can be imported from
// both .tsx component files and plain .ts modules alike.
//
// The card/header style objects match the same treatment already used by
// src/components/PnlDashboardCharts.tsx (chartCardStyle, headerStyle), reused
// here rather than duplicated as a new look.
import type { CSSProperties } from "react";
import { formatCurrency, formatCompactCurrency, formatPercentage } from "../../lib/calculations";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";

export const pnlChartCardStyle: CSSProperties = {
  background: "var(--bg-secondary, #fff)",
  borderRadius: 16,
  padding: "1.5rem",
  border: "1px solid var(--border-color, #e5e7eb)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

export const pnlChartHeaderStyle: CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "var(--text-primary, #111827)",
  marginBottom: "1rem",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

/**
 * A project's display name. Prefers the live claims_projects list (always
 * correct in production, keyed by real project id); falls back to the row's
 * own groupLabel only when the id has no match, which is what happens for
 * every row while running on fixture data (see features/pnlAnalytics/fixtures.ts).
 */
export function resolveProjectName(
  projectId: string,
  groupLabel: string | undefined,
  projectsById: Map<string, string>,
): string {
  return projectsById.get(projectId) ?? groupLabel ?? projectId;
}

// SEQ-182: "hours" added. Kept as this module's own small union rather than
// importing the full PnlUnit vocabulary from lib/pnl, because
// "compact-currency" (the KPI cards' own case, plan section 6.2) has no
// equivalent there. "hours" delegates to formatMetricValue so the tab band,
// this page and every export agree on one "428.5 hrs" rendering.
export type PnlDisplayUnit = "currency" | "compact-currency" | "percent" | "hours";

export function formatPnlMetric(value: number | null, unit: PnlDisplayUnit): string {
  if (value === null) return "N/A";
  if (unit === "percent") return formatPercentage(value);
  if (unit === "compact-currency") return formatCompactCurrency(value);
  if (unit === "hours") return formatMetricValue(value, "hours", 1);
  return formatCurrency(value);
}

/** Australian financial-year-friendly month label, "Jul 2025", from an ISO 'YYYY-MM-01' string. */
export function formatMonthLabel(periodMonth: string): string {
  const [year, month] = periodMonth.split("-");
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = Number(month) - 1;
  return `${MONTHS[idx] ?? month} ${year}`;
}
